import {useAuth} from "@/hooks/use-auth";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  AppWindow,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Database,
  Sparkles,
  RefreshCcw,
  Wand2,
} from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { loadApp, type LoadedApp } from "@/features/apps/load-app";
import { SpecRenderer } from "@/features/apps/spec-renderer";
import { ReactAppRenderer } from "@/features/apps/react-app-renderer";
import { validateSpec } from "@/features/apps/render-spec-tool";
import { validateReactApp } from "@/features/apps/render-react-tool";
import { EditWithAIPanel } from "@/components/apps/edit-with-ai-panel";
import { VersionHistoryDropdown } from "@/components/apps/version-history-dropdown";

export const Route = createFileRoute("/apps/$appId")({
  head: () => ({
    meta: [
      { title: "App — AskBell" },
      { name: "description", content: "View and edit your interactive app." },
    ],
  }),
  component: AppRunnerPage,
  notFoundComponent: () => (
    <AppLayout>
      <div className="flex-1 flex items-center justify-center text-center p-6">
        <div>
          <h1 className="text-ui-xl font-bold mb-2">App not found</h1>
          <p className="text-ui-base text-muted-foreground mb-4">
            The app you're looking for doesn't exist or you don't have access.
          </p>
          <Button asChild>
            <Link to="/apps">Back to apps</Link>
          </Button>
        </div>
      </div>
    </AppLayout>
  ),
});

/* ───────────────── Types ───────────────── */

function AppRunnerPage() {
  const { appId } = useParams({ from: "/apps/$appId" });
  const [state, setState] = useState<LoadedApp>({ status: "loading" });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });

    (async () => {
      try {
        const loaded = await loadApp(appId);
        if (!cancelled) setState(loaded);
      } catch (e) {
        if (!cancelled) {
          setState({
            status: "error",
            error: e instanceof Error ? e.message : String(e),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [appId, reloadKey]);

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="gap-1.5 mb-6 -ml-2"
          >
            <Link to="/apps">
              <ArrowLeft className="h-4 w-4" />
              Back to apps
            </Link>
          </Button>

          {state.status === "loading" && (
            <div className="flex flex-col items-center justify-center py-24 gap-3 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <p className="text-ui-base">Loading app and dataset…</p>
            </div>
          )}

          {state.status === "missing" && (
            <div className="text-center py-16">
              <h1 className="text-ui-xl font-bold mb-2">App not found</h1>
              <p className="text-ui-base text-muted-foreground">
                The app you're looking for doesn't exist or you don't have
                access.
              </p>
            </div>
          )}

          {state.status === "error" && (
            <div className="rounded-ui-md border border-destructive/30 bg-destructive/5 p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-ui-base font-bold text-destructive mb-1">
                    Couldn't load this app
                  </p>
                  <pre className="whitespace-pre-wrap text-ui-sm text-destructive/90 font-mono">
                    {state.error}
                  </pre>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3 gap-1.5"
                    onClick={() => setReloadKey((k) => k + 1)}
                  >
                    <RefreshCcw className="h-3.5 w-3.5" />
                    Retry
                  </Button>
                </div>
              </div>
            </div>
          )}

          {state.status === "ready" && (
            <ReadyView state={state} onReload={() => setReloadKey((k) => k + 1)} />
          )}
        </div>
      </div>
    </AppLayout>
  );
}

/* ───────────────── Ready view ───────────────── */

function ReadyView({
  state,
  onReload,
}: {
  state: Extract<LoadedApp, { status: "ready" }>;
  onReload: () => void;
}) {
  const { app, version, datasets, datasetMeta, primaryParsed, versions } = state;
  const {user}=useAuth();
  const canEdit=Boolean(user && app.user_id===user.id);
  const [editOpen, setEditOpen] = useState(false);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <AppWindow className="h-5 w-5 text-primary" />
        <h1 className="text-ui-2xl font-bold tracking-tight text-foreground">
          {app.title}
        </h1>
        <Badge variant="outline" className="text-ui-xs uppercase tracking-wider">
          {app.kind === "react" ? "Interactive" : "Dashboard"}
        </Badge>
        <Badge variant="outline" className="text-ui-xs tracking-wider">
          v{version.version_number}
        </Badge>
        {version.model_used && (
          <Badge
            variant="outline"
            className="text-ui-xs tracking-wider gap-1"
          >
            <Sparkles className="h-2.5 w-2.5" />
            {version.model_used.split("/").pop()}
          </Badge>
        )}
        <div className="ml-auto flex items-center gap-2">
          <VersionHistoryDropdown
            appId={app.id}
            canEdit={canEdit}
            versions={versions}
            currentVersionId={version.id}
            onSwitched={onReload}
            onDeleted={onReload}
            onRestored={onReload}
          />
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => setEditOpen(true)}
            disabled={!primaryParsed || !canEdit}
            title={
              primaryParsed
                ? "Revise this app with AI"
                : "Dataset is still loading…"
            }
          >
            <Wand2 className="h-3.5 w-3.5" />
            Edit with AI
          </Button>
        </div>
      </div>

      {app.description && (
        <p className="text-ui-base text-muted-foreground mb-4">{app.description}</p>
      )}

      {/* Dataset chips */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {datasetMeta.map((d) => (
          <Badge
            key={d.binding}
            variant="outline"
            className="gap-1.5 font-normal text-foreground/80 border-border"
          >
            <Database className="h-3 w-3 text-primary/80" />
            <span className="truncate max-w-[200px]">{d.filename}</span>
            <span className="text-ui-xs text-muted-foreground">
              {d.row_count.toLocaleString()} rows
            </span>
          </Badge>
        ))}
        <Button
          size="sm"
          variant="ghost"
          className="h-6 gap-1 px-2 text-ui-xs text-muted-foreground hover:text-foreground"
          onClick={onReload}
        >
          <RefreshCcw className="h-3 w-3" />
          Reload
        </Button>
      </div>

      {/* Renderer */}
      {app.kind === "spec" ? (
        <SpecRendererBoundary payload={version.payload} datasets={datasets} />
      ) : (
        <ReactRendererBoundary payload={version.payload} datasets={datasets} />
      )}

      {/* Edit With AI panel */}
      <EditWithAIPanel
        open={editOpen}
        onOpenChange={setEditOpen}
        appId={app.id}
        currentKind={app.kind}
        currentPayload={version.payload}
        currentVersionId={version.id}
        dataset={primaryParsed}
        onApplied={() => onReload()}
      />
    </>
  );
}

/* ───────────────── Renderer wrappers ───────────────── */

function SpecRendererBoundary({
  payload,
  datasets,
}: {
  payload: unknown;
  datasets: Record<string, Array<Record<string, unknown>>>;
}) {
  const validated = validateSpec(payload);
  if (!validated.ok) {
    return (
      <PayloadError
        title="Saved dashboard spec is invalid"
        details={validated.errors}
      />
    );
  }
  return <SpecRenderer spec={validated.spec} datasets={datasets} />;
}

function ReactRendererBoundary({
  payload,
  datasets,
}: {
  payload: unknown;
  datasets: Record<string, Array<Record<string, unknown>>>;
}) {
  const validated = validateReactApp(payload);
  if (!validated.ok) {
    return (
      <PayloadError
        title="Saved React app spec is invalid"
        details={validated.errors}
      />
    );
  }
  // Map dataset binding names → row arrays. The schema lets the AI bind any
  // dataset_id to any binding name; default to "primary" → primary rows.
  const data: Record<string, Array<Record<string, unknown>>> = {};
  const bindings = validated.spec.datasets ?? [
    { dataset_id: "primary", binding: "primary" },
  ];
  for (const b of bindings) {
    data[b.binding] = datasets[b.dataset_id] ?? datasets.primary ?? [];
  }
  return <ReactAppRenderer spec={validated.spec} data={data} height={620} />;
}

function PayloadError({ title, details }: { title: string; details: string }) {
  return (
    <div className="rounded-ui-md border border-destructive/30 bg-destructive/5 p-6">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-ui-base font-bold text-destructive mb-1">{title}</p>
          <pre className="whitespace-pre-wrap text-ui-sm text-destructive/80 font-mono">
            {details}
          </pre>
        </div>
      </div>
    </div>
  );
}

